/* eslint-disable react/jsx-key */
import React, { useEffect, useRef } from 'react';

import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import {
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Box,
  CircularProgress,
  useDisclosure,
} from '@chakra-ui/react';
import { isEmpty } from 'ramda';
import { useTable, useSortBy } from 'react-table';

import BrokersAddNewView from 'components/modules/BrokersAddNew';
import Button from 'components/primitives/Button';
import PaginationView from 'components/primitives/Pagination';
import { Search, Listing as ListingIcon } from 'components/primitives/SVG';
import Typography from 'components/primitives/Typography';
import { useTheme } from 'utils/theme';

import { BrokersGeneratedProps } from './Brokers.props';

const widthPerAccessor = {
  listingType: 150,
  price: 150,
  undefined: undefined,
};

const BrokersView = ({
  data,
  meta,
  isLoading,
  tableOptions,
  setTableOptions,
  refetch,
  ...props
}: BrokersGeneratedProps): JSX.Element => {
  const theme = useTheme();
  const addNewModal = useDisclosure();
  const searchRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const columns = React.useMemo(
    () => [
      {
        Header: 'Owner',
        accessor: 'name',
      },
      {
        Header: 'Company',
        accessor: 'company',
      },
      {
        Header: 'Members',
        accessor: 'teamMembers',
      },
      {
        Header: 'Buying Profiles',
        accessor: 'buyingProfiles',
      },
      {
        Header: 'Date Created',
        accessor: 'createdAt',
      },
      {
        Header: 'Status',
        accessor: 'status',
      },
      {
        Header: 'Action Required',
        accessor: 'actionRequired',
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
  } = useTable(
    {
      data,
      // @ts-ignore
      columns,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortBy: isLoading,
    },
    useSortBy
  );

  const onPaginate = (page: number, pageSize: number) => {
    setTableOptions((prevState) => {
      return {
        ...prevState,
        page: prevState.limit !== pageSize ? 1 : page,
        limit: pageSize,
      };
    });
  };

  useEffect(() => {
    setTableOptions((prevState) => {
      if (!isEmpty(sortBy)) {
        const { id, desc } = sortBy[0];
        return {
          ...prevState,
          sort: id,
          direction: desc ? 'desc' : 'asc',
        };
      } else if (isEmpty(sortBy) && !!prevState.sort) {
        const psCopy = { ...prevState };
        delete psCopy.sort;
        delete psCopy.direction;

        return psCopy;
      }

      return prevState;
    });
  }, [sortBy]);

  return (
    <Flex flexDirection="column">
      <Flex flex={1}>
        <Typography variant="type5" weight="700" color="primary">
          Mortgage Brokers
        </Typography>
        <Flex flex={1} justifyContent="flex-end">
          <Button variant="small" onClick={addNewModal.onOpen}>
            Create New Mortgage Broker
          </Button>
        </Flex>
      </Flex>
      <Flex
        mt={5}
        flex={1}
        borderRadius={10}
        p={5}
        minHeight="500px"
        bg="#fff"
        filter="drop-shadow(0px 2px 10px rgba(149, 149, 149, 0.18))"
        flexDirection="column"
      >
        <Flex
          flexDir="row"
          width="100%"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Flex height="30px" alignItems="center">
            <ListingIcon color="shade10" width={12} height={12} />
            <Flex mx={3}>
              <Typography variant="type8" weight="600" color="shade10">
                ALL BROKERS
              </Typography>
            </Flex>
            {isLoading ? (
              <CircularProgress
                isIndeterminate
                size="20px"
                color={theme.colors.primary}
              />
            ) : (
              <Typography variant="type10" color="shade5">
                {meta.count} Brokers
              </Typography>
            )}
          </Flex>
          <HStack spacing={5}>
            <InputGroup size="sm" width={300}>
              <InputLeftAddon borderRadius={10} backgroundColor="transparent">
                <Search width="14px" height="14px" />
              </InputLeftAddon>
              <Input
                ref={searchRef}
                onChange={(a) => props.onSearchKey(a.target.value)}
                borderLeft="none"
                placeholder="Search"
                borderRadius={10}
              />
            </InputGroup>
          </HStack>
        </Flex>
        <Table size="sm" mt={7} {...getTableProps()}>
          <Thead backgroundColor="shade12">
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <Th
                    {
                      // @ts-ignore
                      ...column.getHeaderProps(column.getSortByToggleProps())
                    }
                  >
                    <Flex flexDirection="row">
                      <Typography variant="type9" weight="600" color="shade5">
                        {column.Header}
                      </Typography>
                      <Flex ml={2}>
                        {
                          // @ts-ignore
                          column.isSorted ? (
                            <Box>
                              {
                                // @ts-ignore
                                column.isSortedDesc ? (
                                  <TriangleDownIcon aria-label="sorted descending" />
                                ) : (
                                  <TriangleUpIcon aria-label="sorted ascending" />
                                )
                              }
                            </Box>
                          ) : (
                            <Box width="12px" height="5px" />
                          )
                        }
                      </Flex>
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row);
              return (
                <Tr
                  backgroundColor={i % 2 !== 0 ? 'shade18' : 'transparent'}
                  cursor="pointer"
                  onClick={() => props.setListing(row.original)}
                  {...row.getRowProps()}
                >
                  {row.cells.map((cell) => (
                    <Td
                      {...cell.getCellProps()}
                      // @ts-ignore
                      width={widthPerAccessor[cell.column.id]}
                    >
                      <Typography variant="type8" color="shade5">
                        {cell.render('Cell')}
                      </Typography>
                    </Td>
                  ))}
                </Tr>
              );
            })}
          </Tbody>
        </Table>

        <Flex mt={5} flexDir="row" width="100%" justifyContent="flex-end">
          <PaginationView
            disabled={isLoading}
            total={meta.count}
            current={meta.page}
            pageSize={tableOptions.limit}
            onChange={onPaginate}
          />
        </Flex>

        <BrokersAddNewView {...addNewModal} refetch={refetch} />
      </Flex>
    </Flex>
  );
};

export default BrokersView;
